/* eslint-disable no-param-reassign */

import { differenceInSeconds } from 'date-fns';
import type {
  GetAllResumeTemplatesResponse,
  GetResumeBuilderResponseById,
  GetResumeTemplateResponse,
} from 'src/openapi';
import {
  DEFAULT_EDUCATION_FIELDS,
  DEFAULT_SPACING,
  languageProficiencyOptions,
  months,
  RESUME_TEMPLATES,
  resumeProgressMapping,
  ResumeTemplateCategory,
  ResumeThemes,
  withMissingSections,
} from 'src/constants/resumeBuilder';
import type {
  AwardsAndAchievements,
  Education,
  ExistingResume,
  Experience,
  ExtendedResumeTemplateCategoryType,
  InterestOrHobbies,
  Language,
  LicensesAndCertification,
  Link,
  PositionOfResponsibility,
  Project,
  Publication,
  ResumeDate,
  ResumeDetails,
  ResumeDraftState,
  ResumeTemplateNames,
  Skill,
  UploadedResume,
  VolunteerExperience,
} from 'src/pages/ResumeBuilder/types';
import parse from 'src/helpers/safeHtml';
import type { BuyCreditsDialogContext } from 'src/components/buyCreditsDialog/types';
import { checkUserPremium, isValidUrl, parseUTCDate } from '../utils';
import { toCalendarDate } from '../profileDates';
import { formatDegreeTitle, formatGradeText } from '../resumeFields';

export const getResumeTheme = (theme?: string | null) =>
  ResumeThemes.find((resumeTheme) => resumeTheme.value === theme);

/* The resume JSON reaches these helpers from an LLM extraction, from a
 * half-filled draft, and from an older saved version, so any field can be
 * missing, null, or a different shape than the mappers below assume. The three
 * coercions keep that handling in one place instead of at every read. */

/** A list to map over. Anything that is not an array -- null, an object, a
 * lone item the model emitted unwrapped -- yields nothing, and null entries
 * inside a real array are dropped, so a mapper never reads off null. */
const asList = (value: unknown): any[] =>
  Array.isArray(value) ? value.filter((item) => item != null) : [];

/** A string to render. React throws on a raw object child and `String()` would
 * hand it "[object Object]", so anything that is not text becomes empty. */
const asText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  return typeof value === 'number' && Number.isFinite(value)
    ? String(value)
    : '';
};

/** Drops the parts of a URL a reader skips: the scheme, the `www.`, and any
 * trailing slash. */
export const stripLinkProtocol = (url?: string | null) =>
  asText(url)
    .trim()
    .replace(/^https?:\/\/(www\.)?/i, '')
    .replace(/\/+$/, '');

/** A readable name for a link, derived from its own URL.
 *
 * Used where links arrive without one -- profile extraction and the resume
 * parser -- so an imported resume reads `in/harshal-takade` rather than the
 * full address. LinkedIn loses its domain because every profile lives under
 * `/in/`, so the handle is the only part that identifies anyone; the rest keep
 * theirs, since `github.com/Harshal2742` says something `Harshal2742` does not.
 */
export const deriveLinkLabel = (url?: string | null) =>
  stripLinkProtocol(url).replace(/^linkedin\.com\//i, '');

/** The proficiency labels `Language` allows. The constant is a plain
 * `string[]`, so a value read off the JSON has to be matched against these
 * before it can be used as one. */
const LANGUAGE_PROFICIENCIES =
  languageProficiencyOptions as Language['proficiency'][];

/** Skills arrive either as plain strings or as `{ name }` objects. */
const toSkill = (skill: any): Skill => ({
  name: asText(typeof skill === 'object' ? skill?.name : skill),
});

const ONGOING_DATE_WORDS =
  /^(present|current|currently|ongoing|now|till date|to date|pursuing)$/i;
const MONTH_NAME =
  /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\b/i;
const MONTH_PREFIXES = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
];
const TIMESTAMP = /^(\d{4})-(\d{1,2})-(\d{1,2})[T ](?=\d)/;
// Digits joined by date separators: "15/06/2019", "06-2019", "2019.06".
const NUMERIC_DATE = /\d+(?:[-/.]\d+)+/g;

/** Whether a resume wrote "Present" (or similar) where an end date would be. */
export const isOngoingDateText = (value: unknown) =>
  ONGOING_DATE_WORDS.test(asText(value).trim());

/** A year (and month only when one was actually given) as a ResumeDate. */
const toResumeDate = (
  year: number,
  month: number | null,
): ResumeDate | null => {
  if (year < 1950 || year > 2100) return null;
  const monthOption =
    month && month >= 1 && month <= 12 ? months[month - 1] : null;
  return {
    month: monthOption ? { ...monthOption } : null,
    year: { value: year, label: String(year) },
  };
};

/** A date as a resume wrote it -- "2019", "2019-06", "2021-08-15", "Mar 2024",
 * "06/2019" -- with the month only when the resume gave one. Mirrors
 * parse_resume_date in backend-api: a lone year stays a year instead of
 * becoming January. "Present" and anything unparseable are null. */
export const parseResumeDateText = (value: unknown): ResumeDate | null => {
  const text = asText(value).trim();
  if (!text || isOngoingDateText(text) || /^(n\/?a|null|none)$/i.test(text)) {
    return null;
  }
  let match = text.match(/^(\d{4})$/);
  if (match) return toResumeDate(Number(match[1]), null);
  match = text.match(/^(\d{4})\s*[-/.]\s*(\d{1,2})$/);
  if (match) return toResumeDate(Number(match[1]), Number(match[2]));
  match = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) return toResumeDate(Number(match[1]), Number(match[2]));
  match = text.match(TIMESTAMP);
  if (match) {
    // The day the timestamp stands for in any timezone (see toCalendarDate),
    // which needs it as ISO: "2019-5-31 18:30" would parse as local or not
    // at all.
    const [head, y, m, d] = match;
    const day = toCalendarDate(
      `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}T${text.slice(head.length)}`,
    );
    return day ? toResumeDate(day.getFullYear(), day.getMonth() + 1) : null;
  }
  const year = text.match(/\b(19|20)\d{2}\b/);
  if (!year) return null;
  const monthName = text.match(MONTH_NAME);
  if (monthName) {
    return toResumeDate(
      Number(year[0]),
      MONTH_PREFIXES.indexOf(monthName[1].slice(0, 3).toLowerCase()) + 1,
    );
  }
  // A number is a month only inside a date written with that year, never the
  // 3 of "Q3 2020"; the first part that can be one wins, so "15/06/2019" and
  // "06/15/2019" are both June.
  const numericDate = (text.match(NUMERIC_DATE) ?? [])
    .map((group) => group.split(/[-/.]/))
    .find((parts) => parts.includes(year[0]));
  const month = numericDate?.find(
    (part) => part.length <= 2 && Number(part) >= 1 && Number(part) <= 12,
  );
  return toResumeDate(Number(year[0]), month ? Number(month) : null);
};

/** The degree and its specialization on one line (see formatDegreeTitle). */
export const formatEducationTitle = (education?: Education | null) =>
  formatDegreeTitle(
    asText(education?.degree?.name || education?.degree?.value || ''),
    asText(
      education?.specialization?.name || education?.specialization?.value || '',
    ),
  );

/** A position of responsibility in the shape every template already draws
 * volunteer experience in -- the two sections share one layout. */
export const positionAsVolunteerEntry = (
  position: PositionOfResponsibility,
): VolunteerExperience => ({
  role: position.role,
  organisation: position.organisation,
  startDate: position.startDate,
  endDate: position.endDate,
  currentlyVolunteering: position.currentlyHolding,
  description: position.description,
  showOnResume: position.showOnResume,
});

/** Rows of an AI-enhanced list matched to the stored rows they rewrite.
 *
 * By identity first (title, company), so a rewrite that adds, drops or
 * reorders a job never lands one job's description on another; by position
 * only when the model returned the list intact and the row carries no
 * identity of its own. Unmatched rows keep their original text. */
const matchEnhancedRows = <T>(
  baseRows: T[],
  enhancedRows: any[],
  identity: (row: any) => string,
) =>
  baseRows.map((row, index) => {
    const key = identity(row);
    if (key) {
      const byIdentity = enhancedRows.find((item) => identity(item) === key);
      if (byIdentity) return byIdentity;
    }
    const positional = enhancedRows[index];
    return enhancedRows.length === baseRows.length &&
      positional &&
      !identity(positional)
      ? positional
      : undefined;
  });

const normalisedKey = (...parts: unknown[]) =>
  parts
    .map((part) => asText(part).trim().toLowerCase())
    .filter(Boolean)
    .join('|');

const experienceIdentity = (row: any) =>
  normalisedKey(
    row?.jobTitle?.name || row?.jobTitle?.value || row?.jobTitle?.label,
    row?.employer?.company_name,
  );

const projectIdentity = (row: any) => normalisedKey(row?.title);

/** "+91-9876543210", "+91 9876543210", or a bare number with no separator.
 * Splitting on the separator unconditionally made a number without one the
 * country code and left `number` undefined -- which formatPhoneNumber renders
 * as nothing, so the phone vanished from the resume entirely. */
const parsePhoneNumber = (phone: unknown) => {
  const text = asText(phone).trim();
  if (!text) return null;

  const withCode = text.match(/^\+?(\d{1,4})[-\s]+(.+)$/);
  return withCode
    ? { number: withCode[2].trim(), countryCode: withCode[1] }
    : { number: text, countryCode: '' };
};

/** The label a link should carry, for a link that arrived without one.
 *
 * Resumes written before `label` existed store only `name` and `url`, and the
 * profile name -- "LinkedIn", "GitHub" -- is the right stand-in: it is what
 * the writer picked when they added the link, and it reads better on a resume
 * than the address it points at. Falls through to the URL's own handle only
 * where there is no name, which is the "Other" case.
 */
export const linkLabelFallback = (link?: Link | null) =>
  asText(link?.name).trim() || deriveLinkLabel(link?.url);

/** A link with its label filled in, used on every path that loads a saved
 * resume so an old one never renders label-less. */
export const withLinkLabel = (link?: Link | null): Link => {
  const existing = asText(link?.label).trim();
  return existing
    ? { ...link, label: existing }
    : { ...link, label: linkLabelFallback(link) };
};

/** What a template prints for a link: the name the writer gave it, or the
 * profile name for a link saved before labels existed. */
export const resumeLinkText = (link?: Link | null) =>
  asText(link?.label).trim() || linkLabelFallback(link);

/** Whether the resume prints this link at all.
 *
 * Needs somewhere to go and something to show. Since a saved link always
 * carries a `name`, in practice only a link with no URL drops out -- the
 * label-less links that used to vanish now fall back to their profile name.
 */
export const isPrintableLink = (link?: Link | null) =>
  !!asText(link?.url).trim() && !!resumeLinkText(link);

/** Backfills link labels on a loaded resume.
 *
 * Applied where saved JSON enters the builder rather than only at render, so
 * the label the resume prints is the one the edit form shows -- otherwise an
 * old link displayed "LinkedIn" on the page while its label box sat empty.
 */
const withLinkLabels = (details: ResumeDetails): ResumeDetails =>
  details?.personalDetails?.links?.length
    ? {
        ...details,
        personalDetails: {
          ...details.personalDetails,
          links: details.personalDetails.links.map(withLinkLabel),
        },
      }
    : details;

export const mapFetchedDataToExistingData = (
  data: GetResumeBuilderResponseById,
): ExistingResume => {
  const resumeMetadata = withLinkLabels(data.resume_meta_data as ResumeDetails);

  return {
    id: data.resume_builder_id!,
    resumeDetails: {
      ...resumeMetadata,
      title: data.title ?? '',
      template: {
        id: data.resume_template_id!,
        name: data.selected_template_name! as ResumeTemplateNames,
      },
      currentVersion: {
        id: data.id!,
        version_no: data.version_no!,
      },
    },
    settings: {
      // All three are nullable on the record and on `Settings`; asserting them
      // non-null only hid the undefined from the type, it did not stop it
      // reaching the preview.
      fontFamily: data.settings_metadata?.fontFamily ?? null,
      fontSize: data.settings_metadata?.fontSize ?? null,
      theme: getResumeTheme(data.settings_metadata?.theme) ?? null,
      // The resume now has a single editable copy; enhancement is applied in
      // place, so there is no separate "AI Enhanced" view to restore.
      view: 'Original',
      // A resume saved before a section existed has a layout that never names
      // it, so backfill on load or the section stays invisible on that resume.
      sectionLayout:
        data.settings_metadata?.sectionLayout &&
        withMissingSections(data.settings_metadata.sectionLayout),
      skillsStyle: data.settings_metadata?.skillsStyle ?? 'chips',
      educationFields:
        data.settings_metadata?.educationFields ?? DEFAULT_EDUCATION_FIELDS,
      spacing: data.settings_metadata?.spacing ?? DEFAULT_SPACING,
    },
    // `Number(undefined)` is NaN, which propagates into the progress bar.
    progress: Number(data.percent_complete) || 0,
    totalDownloadsCount: data.total_download_count ?? 1,
    unlockedTemplates: ((data as any).unlocked_templates ?? []) as string[],
    createdAt: data.created_at ?? '',
    modifiedOn: data.modified_on ?? '',
    // A resume saved before these settings existed has no metadata at all;
    // calculateProgress iterates this list, so undefined would throw there.
    completedSections: data.settings_metadata?.completedSections ?? [],
    currentStep: data.settings_metadata?.currentStep ?? 'personalDetails',
    currentUpdating:
      data.settings_metadata?.currentUpdating ?? 'personalDetails',
    enhancedResumeData: null,
    analysis: (data as any).analysis_metadata ?? null,
  };
};

// Deterministic fingerprint of the resume fields the AI score reads. Mirrors
// the slice's stale-tracking set (experience, projects, shortBio, skills) so
// the persisted analysis can be compared against current content on reload —
// without it, an edit-then-reload cycle would show the stale score as fresh.
export const computeResumeSourceHash = (
  resumeDetails: ResumeDetails | undefined | null,
): string => {
  if (!resumeDetails) return '';
  const scoreRelevant = {
    shortBio: resumeDetails.shortBio ?? '',
    experience: (resumeDetails.experience ?? []).map((e) => ({
      jobTitle: e.jobTitle?.value ?? e.jobTitle?.name ?? '',
      employer: e.employer?.company_name ?? '',
      description: e.description ?? '',
      showOnResume: e.showOnResume,
    })),
    projects: (resumeDetails.projects ?? []).map((p) => ({
      title: p.title ?? '',
      subTitle: p.subTitle ?? '',
      description: p.description ?? '',
      showOnResume: p.showOnResume,
    })),
    skills: (resumeDetails.skills ?? []).map((s) => s.name ?? ''),
  };
  const json = JSON.stringify(scoreRelevant);
  let hash = 5381;
  for (let i = 0; i < json.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) + hash) ^ json.charCodeAt(i);
  }
  // eslint-disable-next-line no-bitwise
  return (hash >>> 0).toString(16);
};

/**
 * Apply an AI-enhanced resume payload on top of the editable resume in place.
 * The enhanced text becomes the working content (it replaces `description` /
 * `shortBio` / `skills` rather than living in a separate non-editable overlay),
 * so the user edits the enhanced version directly. Any leftover overlay fields
 * are cleared. Only fields the AI actually returned are overwritten.
 */
export const applyEnhancedToResume = (state: ResumeDetails, payload: any) => {
  if (!payload) return;

  if (state.experience && Array.isArray(payload.experience)) {
    const matched = matchEnhancedRows(
      state.experience,
      payload.experience,
      experienceIdentity,
    );
    state.experience = state.experience.map((experience, index) => {
      const enhancedDescription = asText(matched[index]?.description);
      return {
        ...experience,
        description: enhancedDescription
          ? `<p>${enhancedDescription}</p>`
          : experience.description,
        description_gpt: undefined,
      };
    });
  }

  if (state.projects && Array.isArray(payload.projects)) {
    const matched = matchEnhancedRows(
      state.projects,
      payload.projects,
      projectIdentity,
    );
    state.projects = state.projects.map((project, index) => {
      const enhancedDescription = asText(matched[index]?.description);
      return {
        ...project,
        description: enhancedDescription
          ? `<p>${enhancedDescription}</p>`
          : project.description,
        description_gpt: undefined,
      };
    });
  }

  const shortBio = asText(payload.shortBio);
  if (shortBio) {
    state.shortBio = `<p>${shortBio}</p>`;
  }

  // The model returns skills as bare strings about as often as it returns
  // `{ name }`, and a bare string reaches the templates as `skill.name ===
  // undefined` -- a section of blank chips.
  const skills = asList(payload.skills)
    .map(toSkill)
    .filter((s) => s.name);
  if (skills.length) {
    state.skills = skills;
  }

  state.enhancedShortBio = undefined;
  state.enhancedSkills = undefined;
};

export const calculateProgress = (
  completedSections: (keyof ResumeDetails)[] | null | undefined,
): number =>
  // An unrecognised section name -- one renamed since the resume was saved --
  // contributes 0 rather than turning the whole total into NaN.
  (Array.isArray(completedSections) ? completedSections : []).reduce(
    (total, section) => total + (resumeProgressMapping[section] ?? 0),
    0,
  );

export const formatMonthYear = (date: ResumeDate | null | undefined) =>
  `${date?.month ? date.month.label : 'Month'} ${date?.year ? date.year.label : 'Year'}`;

export const formatMonthYearRange = (
  startDate: ResumeDate | null | undefined,
  endDate: ResumeDate | null | undefined,
  isOngoing?: boolean,
  ongoingLabel = 'Present',
) =>
  `${formatMonthYear(startDate)}-${isOngoing ? ongoingLabel : formatMonthYear(endDate)}`;

export const nextStep = (currentStep: keyof ResumeDetails) => {
  switch (currentStep) {
    case 'personalDetails':
      return 'education';
    case 'education':
      return 'skills';
    case 'skills':
      return 'experience';
    case 'experience':
      return 'projects';
    case 'projects':
      return 'shortBio';
    case 'shortBio':
      return 'licensesAndCertification';
    default:
      return 'download';
  }
};

export const yearRangeGenerator = () => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => ({
    value: currentYear - i,
    label: `${currentYear - i}`,
  }));
  return years;
};

/* eslint-disable @typescript-eslint/naming-convention */
export const convertUploadedResumeJsonIntoResumeDetails = ({
  id,
  title,
  resumeImage,
  resumeDetails,
}: {
  id: string;
  title: string;
  resumeImage: string | null | undefined;
  resumeDetails: any;
}): UploadedResume => {
  // "2019" stays a year, "Mar 2024" keeps its month, "Present" is no date.
  const mapDate = parseResumeDateText;

  /** The end date and whether the entry is ongoing: a flag, or "Present"
   * written where the end date would be. */
  const mapEnd = (item: any, flag?: unknown) => {
    const ongoing = !!flag || isOngoingDateText(item?.end_date);
    return { endDate: ongoing ? null : mapDate(item?.end_date), ongoing };
  };

  const mapExperience = (work: any): Experience => {
    // Backend emits `company_name` (RESUME_JSON_PROMPT); older callers used `company`.
    const companyName = asText(work.company_name) || asText(work.company);
    const designation = asText(work.designation);
    const location = asText(work.location_details);
    const description = asText(work.description);
    const descriptionGpt = asText(work.description_gpt);
    const { endDate, ongoing } = mapEnd(work, work.is_current_company);
    return {
      jobTitle: designation ? { value: designation, label: designation } : null,
      employer: companyName ? { company_name: companyName } : null,
      startDate: mapDate(work.start_date),
      endDate,
      currentlyWorking: ongoing,
      location: location ? { description: location } : null,
      remote: asText(work.job_location_type).toLowerCase() === 'remote',
      description: description ? `<p>${description}</p>` : '',
      showOnResume: true,
      description_gpt: descriptionGpt ? `<p>${descriptionGpt}</p>` : undefined,
    };
  };

  const mapEducation = (edu: any): Education => {
    const degree = asText(edu.degree);
    const specialization = asText(edu.specialization);
    // RESUME_JSON_PROMPT emits `college_name`, not `institution` -- reading
    // the wrong source key meant this was always null regardless of shape.
    const college = asText(edu.college_name);
    const location = asText(edu.location_details);
    const { endDate, ongoing } = mapEnd(edu, edu.is_currently_studying);
    return {
      degree: degree ? { value: degree } : null,
      specialization: specialization
        ? { value: specialization, label: specialization }
        : null,
      institution: college ? { college_name: college } : null,
      location: location ? { description: location } : null,
      startDate: mapDate(edu.start_date),
      endDate,
      grade: asText(edu.grade) || null,
      currentlyStudying: ongoing,
      showOnResume: true,
    };
  };

  const mapLicenses = (cert: any): LicensesAndCertification => {
    const issuer = asText(cert.issuing_organization);
    return {
      name: asText(cert.title) || null,
      organisation: issuer ? { company_name: issuer } : null,
      issueDate: mapDate(cert.date_obtained),
      expirationDate: mapDate(cert.expiry_date),
      doesNotExpire: false,
      credentialID: asText(cert.credential_id) || null,
      credentialURL: asText(cert.credential_url) || null,
      showOnResume: true,
    };
  };

  const mapProject = (proj: any): Project => {
    const description = asText(proj.description);
    const descriptionGpt = asText(proj.description_gpt);
    const { endDate, ongoing } = mapEnd(proj, proj.is_ongoing);
    return {
      title: asText(proj.title),
      subTitle: asText(proj.subtitle),
      link: asText(proj.project_link),
      startDate: mapDate(proj.start_date),
      endDate,
      currentlyWorking: ongoing,
      description: description ? `<p>${description}</p>` : '',
      showOnResume: true,
      description_gpt: descriptionGpt ? `<p>${descriptionGpt}</p>` : undefined,
    };
  };

  // Older parses list languages as bare strings, newer ones as
  // `{ language, proficiency }` -- and an object assigned straight to a
  // rendered field crashes the preview with "Objects are not valid as a React
  // child". No default level: one the resume never stated is a claim the
  // candidate never made, and the templates print whatever is here.
  const mapLanguage = (lang: any): Language => {
    const proficiency = LANGUAGE_PROFICIENCIES.find(
      (level) =>
        typeof lang?.proficiency === 'string' &&
        level?.toLowerCase() === lang.proficiency.trim().toLowerCase(),
    );
    return {
      language: asText(typeof lang === 'object' ? lang?.language : lang),
      proficiency,
      showOnResume: true,
    };
  };

  const mapInterest = (interest: any): InterestOrHobbies => ({
    interest: asText(
      typeof interest === 'object' ? interest?.interest : interest,
    ),
    showOnResume: true,
  });

  const mapAward = (award: any): AwardsAndAchievements => ({
    title: asText(award.title),
    issuer: asText(award.issuer),
    issueDate: mapDate(award.date),
    description: asText(award.description),
    showOnResume: true,
  });

  const richText = (value: unknown) => {
    const text = asText(value);
    return text ? `<p>${text}</p>` : '';
  };

  const mapPublication = (publication: any): Publication => ({
    title: asText(publication.title),
    publisher: asText(publication.publisher),
    publicationDate: mapDate(publication.date),
    url: asText(publication.url),
    description: richText(publication.description),
    showOnResume: true,
  });

  const mapVolunteer = (volunteer: any): VolunteerExperience => {
    const { endDate, ongoing } = mapEnd(volunteer, volunteer.is_current);
    return {
      role: asText(volunteer.role),
      organisation:
        asText(volunteer.organization) || asText(volunteer.organisation),
      startDate: mapDate(volunteer.start_date),
      endDate,
      currentlyVolunteering: ongoing,
      description: richText(volunteer.description),
      showOnResume: true,
    };
  };

  const mapPosition = (position: any): PositionOfResponsibility => {
    const { endDate, ongoing } = mapEnd(position, position.is_current);
    return {
      role: asText(position.role),
      organisation:
        asText(position.organization) || asText(position.organisation),
      startDate: mapDate(position.start_date),
      endDate,
      currentlyHolding: ongoing,
      description: richText(position.description),
      showOnResume: true,
    };
  };

  // An extraction that failed outright hands us null, and destructuring that
  // throws before any of the mapping runs.
  const {
    name,
    contact,
    summary,
    work_details,
    education_details,
    certifications,
    projects,
    skills,
    interests,
    languages,
    social_links,
    awards_and_achievements,
    publications,
    volunteer_experience,
    positions_of_responsibility,
    headline,
    location,
  } = resumeDetails ?? {};

  // `name` is the one field every mapper above assumed was present: it was
  // read with `.split()` before anything else, so a resume the model could not
  // find a name on took the whole import down.
  const fullName = asText(name).trim();
  const summaryText = asText(summary);
  const nameParts = fullName.split(/\s+/).filter(Boolean);
  // The headline the resume states, else the current or most recent title.
  const roles = asList(work_details);
  const headlineText =
    asText(headline).trim() ||
    asText(
      (roles.find((work: any) => work?.is_current_company) ?? roles[0])
        ?.designation,
    ).trim();
  const locationText = asText(location).trim();
  const links = asList(social_links).map((link: any) =>
    withLinkLabel({
      name: link.link_type,
      url: asText(link.url),
      label: deriveLinkLabel(link.url),
    }),
  );
  // The prompt also returns LinkedIn under contact, sometimes as its only copy.
  const contactLinkedin = asText(contact?.linkedin).trim();
  if (
    contactLinkedin &&
    !links.some((link) => asText(link.url).toLowerCase().includes('linkedin'))
  ) {
    links.push(
      withLinkLabel({
        name: 'LinkedIn',
        url: contactLinkedin,
        label: deriveLinkLabel(contactLinkedin),
      }),
    );
  }

  return {
    id,
    title: title || fullName,
    resumeImage,
    resumeDetails: {
      personalDetails: {
        firstName: nameParts[0] || '',
        // Everything after the first name: "Mohammed Abdul Rahman" kept only
        // "Abdul" when this took the second word.
        lastName: nameParts.slice(1).join(' '),
        email: asText(contact?.email),
        phone: parsePhoneNumber(contact?.phone),
        jobTitle: headlineText
          ? { value: headlineText, label: headlineText }
          : null,
        address: locationText ? { description: locationText } : null,
        // Parsed payloads carry no label, so one is derived above. The URL's
        // own handle reads better than the bare profile name ("in/jane-doe"
        // over "LinkedIn"); `withLinkLabel` covers the case where the parser
        // returned a link type but no usable URL.
        links,
      },
      // Every list is `asList`: the prompt asks for arrays, but a model that
      // finds one job returns the object unwrapped often enough, and `.map`
      // on it is a TypeError rather than a missing section.
      experience: asList(work_details).map(mapExperience),
      education: asList(education_details).map(mapEducation),
      licensesAndCertification: asList(certifications).map(mapLicenses),
      skills: asList(skills).map(toSkill),
      shortBio: summaryText ? `<p>${summaryText}</p>` : '',
      enhancedShortBio: summaryText ? `<p>${summaryText}</p>` : null,
      projects: asList(projects).map(mapProject),
      interestOrHobbies: asList(interests).map(mapInterest),
      languages: asList(languages).map(mapLanguage),
      awardsAndAchievements: asList(awards_and_achievements).map(mapAward),
      publications: asList(publications).map(mapPublication),
      volunteerExperiences: asList(volunteer_experience).map(mapVolunteer),
      positionsOfResponsibility: asList(positions_of_responsibility).map(
        mapPosition,
      ),
    },
  };
};

export const parseResumeData = (input: any): ResumeDetails => {
  // `precision` is the profile's `*_date_precision`: "year" means the month
  // stored alongside it is a placeholder, so it is left off the resume.
  const parseDate = (date: unknown, precision?: string | null) => {
    const text = asText(date);
    if (!text || /^(current|present)$/i.test(text)) {
      return null;
    }
    // Read as the calendar day that was picked, whatever the browser's
    // timezone (see toCalendarDate).
    const parsedDate = toCalendarDate(text);
    if (!parsedDate) {
      return null;
    }
    const year = parsedDate.getFullYear();

    return {
      month: precision === 'year' ? null : months[parsedDate.getMonth()],
      year: { value: year, label: year.toString() },
    };
  };

  const source = input ?? {};
  const meta = source.candidate_meta_details ?? {};
  const headline = asText(meta.heading).trim();
  const address = asText(meta.location_details?.description).trim();

  return {
    title: source.user_details?.first_name || '',
    personalDetails: {
      firstName: source.user_details?.first_name || '',
      lastName: source.user_details?.last_name || '',
      email: source.user_details?.email || '',
      // A number stored without a separator used to become the country code
      // with no number beside it, which renders as no phone at all.
      phone: parsePhoneNumber(source.user_details?.phone_number),
      // The headline the candidate wrote, else the role they are looking for.
      jobTitle: headline
        ? { name: headline, label: headline }
        : {
            id: meta.job_role_id || null,
            name: meta.job_role_name || '',
          },
      address: address ? { description: address } : null,
      links: asList(source.social_links).map((link: any) =>
        withLinkLabel({
          name: link.link_type || 'Other',
          url: asText(link.url),
          label: deriveLinkLabel(link.url),
        }),
      ),
      avatar: source.user_details?.picture || '',
    },
    experience: asList(source.work_details).map((work: any) => ({
      // The title the candidate wrote; the normalised role only when there is
      // none. "Android Developer Intern" read as "Android Developer".
      jobTitle: work.designation
        ? { name: work.designation, label: work.designation }
        : {
            id: work.job_role_id || 0,
            name: work.job_role_name || '',
          },
      employer: work.company_name
        ? { company_name: work.company_name, id: work.company_id || null }
        : null,
      currentlyWorking: work.is_current_company || false,
      startDate: parseDate(work.start_date, work.start_date_precision),
      endDate: work.is_current_company
        ? null
        : parseDate(work.end_date, work.end_date_precision),
      remote: work.job_location_type === 'Remote',
      location: work.location_details || null,
      description: work.description ? `<p>${work.description}</p>` : '',
      showOnResume: true,
    })),
    education: asList(source.education_details).map((edu: any) => ({
      degree: {
        id: edu.degree_id || undefined,
        name: edu.degree_name || '',
      },
      institution: {
        id: edu.college_id || undefined,
        college_name: edu.college_name || null,
        label: edu.college_name || '',
        value: edu.college_name || '',
      },
      // None rather than a made-up "General": the resume prints whatever is here.
      specialization: edu.specialization_name
        ? { label: edu.specialization_name, value: edu.specialization_name }
        : null,
      distanceLearning: false,
      location: edu.location_details || null,
      currentlyStudying: edu.is_currently_studying || false,
      startDate: parseDate(edu.start_date, edu.start_date_precision),
      endDate: edu.is_currently_studying
        ? null
        : parseDate(edu.end_date, edu.end_date_precision),
      grade: formatGradeText(edu.grade, edu.grade_scale),
      showOnResume: true,
    })),
    skills: asList(source.candidate_meta_details?.skills).map((skill: any) => ({
      name: asText(skill.name),
      skill_id: skill.id || 0,
    })),
    // The profile's About is the summary candidates actually write (66K of
    // them); candidate_meta.summary is a rarely-set second copy.
    shortBio:
      source.user_details?.about || meta.summary
        ? `<p>${source.user_details?.about || meta.summary}</p>`
        : '',
    projects: asList(source.projects).map((project: any) => ({
      title: project.title || '',
      subTitle: project.subtitle || project.type || '',
      link: project.project_link || '',
      startDate: parseDate(project.start_date, project.start_date_precision),
      endDate: project.is_ongoing
        ? null
        : parseDate(project.end_date, project.end_date_precision),
      currentlyWorking: !!project.is_ongoing,
      description: project.description ? `<p>${project.description}</p>` : '',
      showOnResume: true,
    })),
    interestOrHobbies: asList(source.interests).map((interest: any) => ({
      interest: interest.interest || '',
      description: interest.description || '',
      showOnResume: true,
    })),
    // Was hardcoded empty, so a candidate's languages never reached a resume
    // prefilled from their profile.
    languages: asList(source.languages).map((lang: any) => ({
      language: lang.language || '',
      proficiency: lang.proficiency || undefined,
      description: lang.description || '',
      showOnResume: true,
    })),
    // The profile has carried certifications all along (resume_details returns
    // them with a signed image url); this was an empty list, so "prefill from
    // profile" silently dropped every certificate the candidate had entered.
    licensesAndCertification: asList(source.certifications).map(
      (cert: any) => ({
        name: cert.name || '',
        // The issuer is free text on the profile. company_id is only set on the
        // rows that happen to point at a real company row.
        organisation: cert.company_name
          ? { company_name: cert.company_name, id: cert.company_id ?? null }
          : null,
        issueDate: parseDate(cert.start_date, cert.start_date_precision),
        expirationDate: parseDate(cert.end_date, cert.end_date_precision),
        // No end date on the profile is what "does not expire" means here.
        doesNotExpire: !cert.end_date,
        credentialID: cert.credential_id || '',
        credentialURL: cert.credential_url || '',
        showOnResume: true,
      }),
    ),
    // Awards were never carried across at all -- the key was simply absent from
    // this object, so a candidate who had filled the section in got an empty
    // Awards block on a resume prefilled from their profile.
    awardsAndAchievements: asList(source.awards).map((award: any) => ({
      title: award.title || '',
      issuer: award.issuer || '',
      issueDate: parseDate(award.issue_date, award.issue_date_precision),
      description: award.description || '',
      showOnResume: true,
    })),
    publications: asList(source.publications).map((publication: any) => ({
      title: publication.title || '',
      publisher: publication.publisher || '',
      publicationDate: parseDate(
        publication.publication_date,
        publication.publication_date_precision,
      ),
      url: publication.url || '',
      description: publication.description || '',
      showOnResume: true,
    })),
    volunteerExperiences: asList(source.volunteer_experiences).map(
      (volunteer: any) => ({
        role: volunteer.role || '',
        organisation: volunteer.organisation || '',
        startDate: parseDate(
          volunteer.start_date,
          volunteer.start_date_precision,
        ),
        endDate: parseDate(volunteer.end_date, volunteer.end_date_precision),
        currentlyVolunteering: !!volunteer.is_current,
        description: volunteer.description || '',
        showOnResume: true,
      }),
    ),
    positionsOfResponsibility: asList(source.positions_of_responsibility).map(
      (position: any) => ({
        role: position.role || '',
        organisation: position.organisation || '',
        startDate: parseDate(
          position.start_date,
          position.start_date_precision,
        ),
        endDate: parseDate(position.end_date, position.end_date_precision),
        currentlyHolding: !!position.is_current,
        description: position.description || '',
        showOnResume: true,
      }),
    ),
  };
};

export const getShortTimeAgo = (date: string) => {
  const now = new Date();
  const localDate = parseUTCDate(date ?? now.toString());
  if (!localDate) return 'now';

  const seconds = differenceInSeconds(now, localDate);

  if (seconds < 5) return 'now';

  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;

  const monthsAgo = Math.floor(days / 30);
  if (monthsAgo < 12) return `${monthsAgo}mo ago`;

  const years = Math.floor(days / 365);
  return `${years}y ago`;
};

export const mapVersionDataToExistingData = (
  data: GetResumeBuilderResponseById,
  currentResumeState: ResumeDraftState,
): ResumeDraftState => {
  const resumeMetadata = withLinkLabels(data.resume_meta_data as ResumeDetails);

  return {
    ...currentResumeState,
    id: data.resume_builder_id!,
    resumeDetails: {
      ...resumeMetadata,
      template: {
        id: data.resume_template_id!,
        name: data.selected_template_name! as ResumeTemplateNames,
      },
      currentVersion: {
        id: data.id,
        version_no: data.version_no,
        created_at: data.created_at,
        modified_on: data.modified_on,
      },
    },
    settings: {
      ...currentResumeState.settings,
      fontFamily: data.settings_metadata?.fontFamily!,
      fontSize: data.settings_metadata?.fontSize!,
      theme: getResumeTheme(data.settings_metadata?.theme)!,
      // Each version is a single editable copy; no separate enhanced overlay.
      view: 'Original',
      sectionLayout: withMissingSections(
        data.settings_metadata?.sectionLayout ??
          currentResumeState.settings.sectionLayout,
      ),
      skillsStyle:
        data.settings_metadata?.skillsStyle ??
        currentResumeState.settings.skillsStyle ??
        'chips',
      educationFields:
        data.settings_metadata?.educationFields ??
        currentResumeState.settings.educationFields ??
        DEFAULT_EDUCATION_FIELDS,
      spacing:
        data.settings_metadata?.spacing ??
        currentResumeState.settings.spacing ??
        DEFAULT_SPACING,
    },
    isEnhanceResumeDataLoading: false,
    progress: Number(data.percent_complete)!,
    totalDownloadsCount: data.total_download_count ?? 0,
    // A version can legitimately carry no `settings_metadata` -- V1 of an older
    // resume has none, and the job-fit rewrite saves its builder copy without
    // any so the loader's own defaults apply. The bare `!` here made all three
    // `undefined` for those.
    //
    // These two fall back the way `mapFetchedDataToExistingData` does, not to
    // the version being replaced: they describe that version's own progress,
    // and inheriting them would show one version's completion against
    // another's content. `completedSections` is also what `calculateProgress`
    // iterates, so undefined throws there.
    completedSections: data.settings_metadata?.completedSections ?? [],
    currentStep: data.settings_metadata?.currentStep ?? 'personalDetails',
    // Carried through untouched, because it is not the version's property: it
    // is which section the candidate has open in the editor, and switching what
    // they are viewing has no business reaching over and folding it -- or
    // unfolding it.
    //
    // Not a `??` chain either. Closed is stored as `null` (see the accordion's
    // `onToggle`), and `null ?? 'personalDetails'` is `'personalDetails'` -- so
    // every fallback here forced a deliberately-closed section back open. The
    // version's own stored value is ignored for the same reason.
    currentUpdating: currentResumeState.currentUpdating,
    enhancedResumeData: null,
    // Each version carries the analysis it was scored with, so switching
    // versions shows that version's own score instead of the one belonging to
    // whichever version happened to be open before.
    analysis: (data as any).analysis_metadata ?? null,
  };
};

export const handleTemplatePersonalName = (
  firstName?: string | null,
  lastName?: string | null,
) => {
  if (firstName) {
    return `${firstName} ${lastName || ''}`;
  }
  return '';
};

/** Every caller reads a link straight off the resume, where the URL is
 * nullable -- and most of them assert it non-null. `url.startsWith` on a null
 * threw while rendering, taking the whole preview down with it. */
export const formatResumeUrl = (url?: string | null) => {
  const trimmed = asText(url).trim();
  if (!trimmed) return '';

  return trimmed.startsWith('http://') || trimmed.startsWith('https://')
    ? trimmed
    : `https://${trimmed}`;
};

const SOCIAL_URL_PLACEHOLDER_VALUES = new Set([
  'null',
  'undefined',
  'n/a',
  'na',
  'none',
  '-',
  'test',
]);

// Guards the social-icon row on profile/chat views: rows with junk data (placeholder
// text, plain names, multiple URLs jammed together) shouldn't render a clickable icon
// that misleads viewers into thinking it links somewhere real.
export const isDisplayableSocialLinkUrl = (url?: string | null): boolean => {
  if (!url) return false;
  const value = url.trim();
  if (!value || SOCIAL_URL_PLACEHOLDER_VALUES.has(value.toLowerCase())) {
    return false;
  }
  if (/\s/.test(value) || !value.includes('.')) return false;
  return isValidUrl(formatResumeUrl(value));
};

export const formatResumeTemplateDate = (
  startDate?: ResumeDate | null,
  endDate?: ResumeDate | null,
  isRemote?: boolean,
) => {
  if (!startDate?.year?.label) {
    // Only an end date ("Class XII, 2020"): print that rather than nothing.
    if (isRemote) return 'Present';
    if (!endDate?.year?.label) return '';
    return `${endDate?.month?.label?.slice(0, 3) ?? ''} ${endDate.year.label}`.trim();
  }

  const startMonth = startDate?.month?.label?.slice(0, 3) ?? '';
  const startYear = startDate?.year?.label ?? '';

  let customEndDate = '';
  if (isRemote) {
    customEndDate = 'Present';
  } else if (endDate?.year?.label) {
    const endMonth = endDate?.month?.label?.slice(0, 3) ?? '';
    const endYear = endDate?.year?.label ?? '';
    customEndDate = `${endMonth} ${endYear}`.trim();
  }

  // Trimmed the same way the end side already is: a start date carrying only
  // a year would otherwise open the line with a stray space.
  const start = `${startMonth} ${startYear}`.trim();
  return customEndDate ? `${start} - ${customEndDate}` : start;
};

/** A certificate's validity: issued and, when it expires, until when. */
export const formatCertificationValidity = (
  certification?: LicensesAndCertification | null,
) => {
  const issued = formatResumeTemplateDate(certification?.issueDate);
  const expires =
    certification?.doesNotExpire || !certification?.expirationDate
      ? ''
      : formatResumeTemplateDate(certification.expirationDate);
  if (issued && expires) return `${issued} - ${expires}`;
  if (expires) return `Expires ${expires}`;
  return issued;
};

/** "Credential ID: AWS-123", or nothing. */
export const formatCredentialId = (
  certification?: LicensesAndCertification | null,
) => {
  const id = asText(certification?.credentialID).trim();
  return id ? `Credential ID: ${id}` : '';
};

export const formatPhoneNumber = (phone: {
  countryCode?: string | null;
  number?: string | null;
}) => {
  if (!phone?.number) return '';

  const countryCode = phone.countryCode ? `+${phone.countryCode} ` : '';
  return `${countryCode}${phone.number}`;
};

/** Normalises text pasted from Word, Google Docs or a web page before it is
 * rendered into a template.
 *
 * The important case is the non-breaking space. Editors emit runs of `&nbsp;`
 * between words, and a browser treats a run joined by them as ONE unbreakable
 * word -- so `overflow-wrap: break-word` chops it at the column edge, mid-word,
 * at a position that shifts as the column resizes. It also leaves the sentence
 * as a single token for anything extracting text from the exported PDF.
 *
 * This runs on the HTML source, so the entity spellings have to be handled
 * alongside the literal characters -- `&nbsp;` is still six ASCII characters
 * here and is only decoded later, by the parser.
 *
 * U+200C/U+200D are preserved: load-bearing in Indic scripts and emoji
 * sequences, and neither is a line-break opportunity. */
export const stripInvisibleCharacters = (text: string) =>
  text
    // Entity forms first -- still undecoded at this point.
    .replace(/&(nbsp|#160|#x0*a0);/gi, ' ')
    .replace(
      /&(shy|#173|#x0*ad|zwsp|#8203|#x0*200b|ZeroWidthSpace|#65279|#x0*feff);/gi,
      '',
    )
    // Every Unicode format character: soft hyphen, zero-width space, BOM,
    // word joiner, bidi controls.
    .replace(/\p{Cf}/gu, (char) =>
      char === '\u200C' || char === '\u200D' ? char : '',
    )
    // Line/paragraph separators and stray C0/C1 controls, minus tab/newline.
    .replace(
      // eslint-disable-next-line no-control-regex
      /[\u2028\u2029\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g,
      '',
    )
    .replace(/\u00A0/g, ' ');

/** Avatars are base64 data URIs -- megabytes of characters with nothing to
 * normalise, so walking them would cost real time for no gain. */
const UNNORMALISED_KEYS = new Set(['avatar', 'resumeImage']);

/** Runs `stripInvisibleCharacters` over every string in the resume, so the fix
 * reaches all fields (skills, names, email, education, links...) and not just
 * the rich-text ones. Applied once where the templates receive their data,
 * rather than in each of the 19 templates' renderers. */
export const normalizeResumeText = <T>(value: T): T => {
  if (typeof value === 'string') {
    return stripInvisibleCharacters(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeResumeText) as T;
  }
  if (value && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    const next: Record<string, unknown> = {};
    Object.keys(source).forEach((key) => {
      next[key] = UNNORMALISED_KEYS.has(key)
        ? source[key]
        : normalizeResumeText(source[key]);
    });
    return next as T;
  }
  return value;
};

export const formatResumeEnhancedText = (
  originalText: string | null | undefined,
  enhancedText: string | null | undefined,
  isMini: boolean,
  view: string,
) => {
  const render = (html: string) => parse(stripInvisibleCharacters(html));

  if (
    !isMini &&
    view === 'AI Enhanced' &&
    enhancedText &&
    enhancedText.trim().length > 12
  ) {
    return render(enhancedText);
  }

  return originalText && originalText.trim().length > 12
    ? render(originalText)
    : '';
};

export const getAvailableThemesForTemplate = (templateName: string) => {
  if (!templateName) {
    return [];
  }
  const template = RESUME_TEMPLATES.find(
    (t) => t.name === templateName,
  ) as any as { name: string; availableThemes: string };

  return template
    ? ResumeThemes.filter((theme) =>
        template.availableThemes.includes(theme.value),
      )
    : [];
};

export const isResumeTemplateSinglePage = (templateName: string) => {
  const template = RESUME_TEMPLATES.find(
    (t) => t.name === templateName,
  ) as any as { name: string; isSinglePage: boolean };

  return template ? template.isSinglePage : false;
};

export const resumeHasBackground = (templateName: string) => {
  const template = RESUME_TEMPLATES.find(
    (t) => t.name === templateName,
  ) as any as { name: string; isSinglePage: boolean; hasBackground: boolean };

  return template ? template.hasBackground : false;
};

export const ExtendedResumeTemplateCategory: ExtendedResumeTemplateCategoryType[] =
  ['All Templates', ...ResumeTemplateCategory];

/** Flattens an infinite `getResumeTemplates` result into one template list. */
export const selectFlatTemplates = (
  result: { data?: { pages: GetAllResumeTemplatesResponse[] } } | undefined,
): GetResumeTemplateResponse[] =>
  result?.data?.pages.flatMap((page) => page.all_resume_templates ?? []) ?? [];

export const getThemesForTemplate = (template: GetResumeTemplateResponse) => {
  if (!template?.all_themes || template.all_themes.length === 0) {
    // Nothing configured on the record, so fall back to the palette the
    // template itself ships with. A template defined in code with its own
    // colours should offer them without an admin first re-saving the record --
    // and one defined with none still returns [], which hides the card.
    return getAvailableThemesForTemplate(template?.title ?? '');
  }
  return ResumeThemes.filter((theme) =>
    template?.all_themes?.includes(theme.value),
  );
};

export const getDefaultThemeForTemplate = (
  template: GetResumeTemplateResponse,
) => {
  if (!template?.default_theme) {
    // Same fallback: the first colour in the template's own palette is the one
    // it was designed in, which beats the global default blue.
    const own = getAvailableThemesForTemplate(template?.title ?? '');
    return own[0] ?? ResumeThemes[0];
  }
  return (
    ResumeThemes.find((theme) => template.default_theme === theme.value) ||
    ResumeThemes[0]
  );
};

export const getCompletedSections = (
  resumeDetails: ResumeDetails | null | undefined,
): string[] => {
  const completedSections: string[] = [];
  if (!resumeDetails) return completedSections;

  if (
    resumeDetails.personalDetails &&
    resumeDetails.personalDetails.firstName &&
    resumeDetails.personalDetails.firstName.trim() !== ''
  ) {
    completedSections.push('personalDetails');
  }

  if (
    resumeDetails.experience &&
    resumeDetails.experience.some(
      (exp) => exp.jobTitle?.value || exp.jobTitle?.name || exp.jobTitle?.label,
    )
  ) {
    completedSections.push('experience');
  }

  if (
    resumeDetails.education &&
    resumeDetails.education.some(
      (edu) =>
        (edu.degree && (edu.degree.value || edu.degree.name)) ||
        edu.degree?.label,
    )
  ) {
    completedSections.push('education');
  }

  if (
    resumeDetails.licensesAndCertification &&
    resumeDetails.licensesAndCertification.some(
      (cert) => cert.name && cert.name.trim() !== '',
    )
  ) {
    completedSections.push('licensesAndCertification');
  }

  if (
    resumeDetails.skills &&
    resumeDetails.skills.some((skill) => skill.name && skill.name.trim() !== '')
  ) {
    completedSections.push('skills');
  }

  if (resumeDetails.shortBio && resumeDetails.shortBio.trim() !== '') {
    completedSections.push('shortBio');
  }

  if (
    resumeDetails.projects &&
    resumeDetails.projects.some(
      (project) => project.title && project.title.trim() !== '',
    )
  ) {
    completedSections.push('projects');
  }

  if (
    resumeDetails.interestOrHobbies &&
    resumeDetails.interestOrHobbies.some(
      (interest) => interest.interest && interest.interest.trim() !== '',
    )
  ) {
    completedSections.push('interestOrHobbies');
  }

  if (
    resumeDetails.languages &&
    resumeDetails.languages.some(
      (lang) => lang.language && lang.language.trim() !== '',
    )
  ) {
    completedSections.push('languages');
  }

  if (
    resumeDetails.awardsAndAchievements &&
    resumeDetails.awardsAndAchievements.some(
      (award) => award.title && award.title.trim() !== '',
    )
  ) {
    completedSections.push('awardsAndAchievements');
  }

  if (resumeDetails.publications?.some((item) => item.title?.trim())) {
    completedSections.push('publications');
  }

  if (resumeDetails.volunteerExperiences?.some((item) => item.role?.trim())) {
    completedSections.push('volunteerExperiences');
  }

  if (
    resumeDetails.positionsOfResponsibility?.some((item) => item.role?.trim())
  ) {
    completedSections.push('positionsOfResponsibility');
  }

  return completedSections;
};

export const getDeviceInfo = (
  userAgent: string,
): { isIOS: boolean; device: string } => {
  const isIOS =
    !/Android/.test(userAgent) &&
    !/Windows/.test(userAgent) &&
    (/iPhone|iPad/.test(userAgent) ||
      (/Macintosh/.test(userAgent) &&
        /Safari/.test(userAgent) &&
        !/Chrome/.test(userAgent)));

  let device = 'Unknown Device';

  if (/iPhone/.test(userAgent)) {
    device = 'iPhone';
  } else if (/iPad/.test(userAgent)) {
    device = 'iPad';
  } else if (
    /Macintosh/.test(userAgent) &&
    /Safari/.test(userAgent) &&
    !/Chrome/.test(userAgent)
  ) {
    device = 'Mac (Safari)';
  } else if (/Macintosh/.test(userAgent) && /Chrome/.test(userAgent)) {
    device = 'Mac (Chrome)';
  } else if (/Android/.test(userAgent)) {
    device = 'Android';
  } else if (/Windows/.test(userAgent)) {
    device = 'Windows';
  } else if (/Linux/.test(userAgent)) {
    device = 'Linux';
  }

  return { isIOS, device };
};

/** Who may use a template. A design is either free for everyone or part of Pro
 * (`is_premium`); credits no longer buy one. Mirrors the backend's
 * `assert_template_allowed`, so every surface tells a candidate the same thing
 * the server will enforce.
 *
 * `unlocked`: bought for this one resume back when designs were sold for
 * credits -- it stays usable on that resume. Kept here rather than in a card so
 * the gallery, the preview and the tailored-resume picker can never drift.
 */
export type TemplateAccess = 'free' | 'unlocked' | 'included' | 'locked';

/** Whether Pro designs are this user's: a Pro window, or a campus ambassador's
 * Pro, which has no expiry of its own and which the server counts too
 * (is_user_premium_profile_active). Without it ambassadors were pitched Pro for
 * designs the server would have let them use. `is_ambassador_premium` is read
 * off `/user/self`, ahead of the generated client knowing the field. */
export const canUseProDesigns = (user?: {
  premium_profile_expires_on?: string | null;
}) =>
  checkUserPremium(user?.premium_profile_expires_on ?? undefined) ||
  !!(user as { is_ambassador_premium?: boolean } | undefined)
    ?.is_ambassador_premium;

export const getTemplateAccess = (
  template: GetResumeTemplateResponse,
  isUserPremium: boolean,
  isUnlocked?: boolean,
): TemplateAccess => {
  if (!template?.is_premium) return 'free';
  if (isUnlocked) return 'unlocked';
  return isUserPremium ? 'included' : 'locked';
};

export const TEMPLATE_ACCESS_LABEL: Record<TemplateAccess, string> = {
  free: 'Free',
  unlocked: 'Unlocked',
  included: 'Included in Pro',
  locked: 'Pro',
};

/** The Pro pitch, opened from a Pro design: what the candidate was reaching for,
 * not the generic "out of credits" story -- and no top-up, which would not
 * unlock it. */
export const PRO_TEMPLATE_PAYWALL: BuyCreditsDialogContext = {
  title: 'This design is part of Pro',
  subtitle: 'Pro unlocks every resume design, on every resume you make.',
  hideTopUp: true,
  returnHere: true,
  lead: 'ats',
};
